import { Component, h } from 'preact'
import GroupDescription from './GroupDescription'
import Axios from 'axios'
import GroupActionButtons from './GroupActionButtons'
import PrivateGroupPlaceholder from './PrivateGroupPlaceholder'
import GroupMembers from './GroupMembers'
import TopicListItemView, { TopicInfo } from '../topic-list-view/TopicListItemView'
import TopicListItem from '../topic-list-view/TopicListItem'
import { common } from '../common'
import { UserData } from '../event/EventAttendees'
import FeaturedTopicsWidget, {
  FeaturedEvent,
  FeaturedProductUpdate,
  FeaturedTopic
} from '../widget-featured-topics/index.sync'
import { ProductAreaType } from '../../control-components/product-areas/ProductAreaSelect'

export interface GroupData {
  id: number
  title: string
  description: string
  groupType: number
  curatedContent: {
    curatedTopics: Array<{id: number, type: string}>
    title: string
  }
}

interface Props {
  group: GroupData
  csrfToken: string
  topics: TopicInfo[]
  attachmentCdn: string
  isGuest: boolean
  members: UserData[]
  isCurrentUserMember: boolean
  membershipStatus: string
  allProductAreas: ProductAreaType[]
  featuredTopics: Array<FeaturedTopic|FeaturedEvent|FeaturedProductUpdate>
}

interface State {
  userJoined: boolean
  userSubscribed: boolean
  isLoading: boolean
  requestSent: boolean
}

export class Group extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      userJoined: props.isCurrentUserMember,
      isLoading: false,
      requestSent: props.membershipStatus === 'requested',
      userSubscribed: true
    }

    this.joinGroup = this.joinGroup.bind(this)
    this.leaveGroup = this.leaveGroup.bind(this)
    this.requestJoin = this.requestJoin.bind(this)
    this.subscribe = this.subscribe.bind(this)
    this.unsubscribe = this.unsubscribe.bind(this)
  }

  async componentDidMount () {
    if (!this.props.isCurrentUserMember) {
      return
    }

    try {
      await Axios.get(`/subscribers/subforum/${this.props.group.id}`)
      this.setState({ userSubscribed: true })
    } catch (e) {
      this.setState({ userSubscribed: false })
    }
  }

  async joinGroup (onSucess?: Function) {
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    this.setState({ isLoading: true })

    try {
      const isPublicGroup = this.props.group.groupType === 4
      await Axios.post(`/group/${this.props.group.id}/join`, { csrfToken: this.props.csrfToken })
      this.setState({ userJoined: isPublicGroup, isLoading: false, userSubscribed: isPublicGroup })
      if (onSucess) {
        onSucess()
      }
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async leaveGroup () {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`/group/${this.props.group.id}/leave`, { csrfToken: this.props.csrfToken })
      this.setState({ userJoined: false, isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async subscribe () {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`/subscribers/subforum/${this.props.group.id}`, { csrfToken: this.props.csrfToken })
      this.setState({ isLoading: false, userSubscribed: true })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async unsubscribe () {
    this.setState({ isLoading: true })

    try {
      await Axios.delete(`/subscribers/subforum/${this.props.group.id}`, { data: { csrfToken: this.props.csrfToken } })
      this.setState({ isLoading: false, userSubscribed: false })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async requestJoin () {
    this.joinGroup(() => {
      this.setState({ requestSent: true })
    })
  }

  render ({ group, topics, isGuest, attachmentCdn, members, featuredTopics, allProductAreas = [] }: Props,
    { userJoined, userSubscribed, isLoading, requestSent }: State) {
    const { description } = group
    const isNewTopicListViewEnabled = common.isModuleActive('topicItemView')
    const isPrivateGroup = group.groupType === 5

    return (
      <div className='group-detail-wrapper'>
        {(userJoined || !isPrivateGroup) &&
          <div className='group-featured-topics-container'>
            <GroupActionButtons
              joinGroup={this.joinGroup}
              leaveGroup={this.leaveGroup}
              subscribe={this.subscribe}
              unsubscribe={this.unsubscribe}
              isLoading={isLoading}
              isUserMember={userJoined}
              isUserSubscribed={userSubscribed}
              groupId={group.id}
            />
            <FeaturedTopicsWidget
              featuredTopics={featuredTopics}
              title={group.curatedContent.title}
              topicsFullWidth={0}
              topicIds={group.curatedContent.curatedTopics}
              allProductAreas={allProductAreas}
            />
          </div>}
        <div className='sitewidth group-detail'>
          <div className='col col--main has--side qa-div-main'>
            <div className={`${isNewTopicListViewEnabled ? 'list-widget-wrapper' : 'box'}`}>
              {(userJoined || !isPrivateGroup) &&
                topics.map(topic => {
                  return isNewTopicListViewEnabled
                    ? (
                      <TopicListItemView
                        topic={topic}
                        isGuest={isGuest}
                        attachmentCdn={attachmentCdn}
                        hideCategoryMetadata
                      />
                      )
                    : (
                      <TopicListItem
                        tab={0}
                        topic={topic}
                        isGuest={isGuest}
                        attachmentCdn={attachmentCdn}
                        hideCategoryMetadata
                      />
                      )
                })}
              {isPrivateGroup && !userJoined &&
                <PrivateGroupPlaceholder
                  onRequest={this.requestJoin}
                  isLoading={isLoading}
                  requestPending={requestSent}
                />}
            </div>
          </div>
          <div className='col col--side Sidebarmodule qa-div-sidebar'>
            <GroupDescription description={description} />
            {(userJoined || !isPrivateGroup) &&
              <GroupMembers members={members} />}
          </div>
        </div>
      </div>
    )
  }
}

export default Group
